import { useEffect, useState, useRef } from 'react';

type OverpassOpts = {
  city?: string;
  types?: string[];
  enabled?: boolean;
  endpoint?: string;
  // optional key to force refetch (e.g. retry counter)
  fetchKey?: any;
  // allow using public Overpass instances (defaults to false). Safety flag to avoid accidental rate-limited calls.
  allowPublicEndpoint?: boolean;
  // optional bbox: "south,west,north,east" (decimal degrees). If provided, bbox query is used instead of area(name).
  bbox?: string;
  // optional center location query. When provided, we call Overpass with around:radius using lat/lon.
  center?: { lat: number; lon: number; radius?: number };
};

// simple in-memory cache per session to avoid repeated requests
const CACHE_TTL = 1000 * 60 * 5; // 5 minutes
const CACHE_VERSION = 'v2';
const cache = new Map<string, { ts: number; data: any[] }>();

const COMMONS_FILE_REGEX = /^(File|Image):/i;

const buildCommonsUrl = (raw?: string) => {
  if (!raw) return undefined;
  const trimmed = String(raw).trim();
  if (!trimmed) return undefined;
  if (COMMONS_FILE_REGEX.test(trimmed)) {
    const withoutPrefix = trimmed.replace(COMMONS_FILE_REGEX, '').trim();
    if (!withoutPrefix) return undefined;
    const encoded = encodeURIComponent(withoutPrefix.replace(/ /g, '_'));
    return `https://commons.wikimedia.org/wiki/Special:FilePath/${encoded}?width=800`;
  }
  return undefined;
};

export function useOverpassPois(opts: OverpassOpts) {
  const { city, types = [], enabled = false, endpoint, fetchKey, bbox, allowPublicEndpoint, center } = opts || ({} as OverpassOpts);
  // Treat "poi" as a generic bucket (no amenity filter). Remove falsy entries as well.
  const amenityTypes = (types || []).map(t => String(t || '').trim()).filter(t => t && t !== 'poi');
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const retryRef = useRef<number>(0);
  const debounceRef = useRef<number | null>(null);

  const stableEndpoint = endpoint ?? null;
  const stableFetchKey = fetchKey ?? null;
  const stableBbox = bbox ?? null;
  const stableAllowPublic = allowPublicEndpoint ?? false;
  const stableCenter = center && typeof center.lat === 'number' && typeof center.lon === 'number'
    ? {
        lat: Number(center.lat),
        lon: Number(center.lon),
        radius: typeof center.radius === 'number' ? Number(center.radius) : undefined,
      }
    : null;
  const stableCenterKey = stableCenter
    ? `${stableCenter.lat.toFixed(6)},${stableCenter.lon.toFixed(6)},${typeof stableCenter.radius === 'number' ? stableCenter.radius : ''}`
    : '';

  useEffect(() => {
    // clear any pending debounce on unmount or dependency change
    return () => { if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; } if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; } };
  }, []);

  useEffect(() => {
    if (!enabled) {
      // if disabled, clear results but keep cache intact
      setData([]);
      setLoading(false);
      setError(null);
      return;
    }
    if (!city && !stableBbox && !stableCenter) return;

    const normalizedBbox = stableBbox ? String(stableBbox).trim() : '';
    const cacheKey = `${CACHE_VERSION}:${String(city || '').toLowerCase()}:${(types || []).join(',')}:${normalizedBbox}:${stableCenterKey}`;
    const shouldUseCache = stableFetchKey === null;

    if (shouldUseCache) {
      const cached = cache.get(cacheKey);
      if (cached && (Date.now() - cached.ts) < CACHE_TTL) {
        setData(cached.data);
        setLoading(false);
        setError(null);
        return;
      }
    }

    let active = true;
    setLoading(true);
    setError(null);

    const doFetch = async () => {
      // Abort previous
      if (abortRef.current) { abortRef.current.abort(); }
      const controller = new AbortController();
      abortRef.current = controller;

      const buildQuery = () => {
        const genericSelectors = ['["amenity"]', '["tourism"]', '["shop"]', '["leisure"]'];
        const normalizedSelectors = amenityTypes.length > 0
          ? amenityTypes.map(t => `["amenity"="${t}"]`)
          : genericSelectors;

        // If bbox is provided use a bbox query (matches your curl test)
        if (stableBbox && typeof stableBbox === 'string') {
          const parts = stableBbox.split(',').map(p => parseFloat(p.trim()));
          if (parts.length === 4 && parts.every(n => !Number.isNaN(n))) {
            const [south, west, north, east] = parts;
            const bboxSelectors = normalizedSelectors
              .map(sel => `node(${south},${west},${north},${east})${sel};\nway(${south},${west},${north},${east})${sel};\nrelation(${south},${west},${north},${east})${sel};`)
              .join('\n');
            return `\n[out:json][timeout:25];\n(\n${bboxSelectors}\n);\nout center qt;`;
          }
        }

        // Otherwise, if a center point is provided, query within a radius using around.
        if (stableCenter) {
          const radiusRaw = typeof stableCenter.radius === 'number' && !Number.isNaN(stableCenter.radius)
            ? stableCenter.radius
            : 1500;
          const clampedRadius = Math.max(100, Math.min(radiusRaw, 20000));
          const lat = stableCenter.lat;
          const lon = stableCenter.lon;
          const aroundSelectors = normalizedSelectors
            .map(sel => `node(around:${clampedRadius},${lat},${lon})${sel};\nway(around:${clampedRadius},${lat},${lon})${sel};\nrelation(around:${clampedRadius},${lat},${lon})${sel};`)
            .join('\n');
          return `\n[out:json][timeout:25];\n(\n${aroundSelectors}\n);\nout center qt;`;
        }

        const areaSelectors = normalizedSelectors
          .map(sel => `node${sel}(area.searchArea); way${sel}(area.searchArea); relation${sel}(area.searchArea);`)
          .join('\n');

        return `\n[out:json][timeout:25];\narea["name"="${city}"]->.searchArea;\n(\n${areaSelectors}\n);\nout center qt;`;
      };

      const q = buildQuery();
      const url = stableEndpoint || 'https://overpass-api.de/api/interpreter';

      // Safety: don't call the public Overpass endpoint unless explicitly allowed.
      const isPublicOverpass = url.includes('overpass-api.de') || url.includes('overpass.openstreetmap') || url.includes('overpass.kumi');
      if (isPublicOverpass && !stableAllowPublic) {
        // surface a special error so UI can show friendly message and avoid spamming logs
        setError('public-endpoint-disabled');
        setLoading(false);
        return;
      }

      try {
        const res = await fetch(url, { method: 'POST', body: q, headers: { 'Content-Type': 'text/plain' }, signal: controller.signal });

        if (!active) return;

        if (res.status === 429) {
          // Too many requests – set user-facing error and stop automatic retries after a few attempts
          const prevRetry = (retryRef.current ?? 0) as number;
          retryRef.current = prevRetry + 1;
          const retryAfter = res.headers.get('Retry-After');
          const waitMs = retryAfter ? parseInt(retryAfter, 10) * 1000 : Math.min(1000 * Math.pow(2, Math.min((retryRef.current ?? 0), 5)), 30000);
          setError('rate-limited');
          setLoading(false);
          // do not auto-loop forever; schedule a single retry if under limit
          if ((retryRef.current ?? 0) <= 2) {
            setTimeout(() => { if (active) doFetch(); }, waitMs);
          }
          return;
        }

        if (res.status === 504) {
          setError('gateway-timeout');
          setLoading(false);
          return;
        }

        if (!res.ok) {
          const text = await res.text().catch(() => 'unknown');
          setError(`http:${res.status}`);
          setLoading(false);
          return;
        }

        const json = await res.json().catch(() => null);
        const elems = json && Array.isArray(json.elements) ? json.elements : [];
        const mapped = elems.map((el: any) => {
          const title = (el.tags && (el.tags.name || el.tags.amenity || el.tags.shop || el.tags.tourism)) || 'OSM POI';
          const addressParts: string[] = [];
          if (el.tags) {
            if (el.tags['addr:street']) addressParts.push(el.tags['addr:street']);
            if (el.tags['addr:housenumber']) addressParts.push(el.tags['addr:housenumber']);
            if (el.tags['addr:city']) addressParts.push(el.tags['addr:city']);
          }
          const address = addressParts.join(' ').trim() || undefined;
          const id = `osm:${el.type}:${el.id}`;
          const lat = typeof el.lat === 'number' ? el.lat : (el.center && typeof el.center.lat === 'number' ? el.center.lat : undefined);
          const lon = typeof el.lon === 'number' ? el.lon : (el.center && typeof el.center.lon === 'number' ? el.center.lon : undefined);

          let coverImage: string | undefined;
          if (el.tags) {
            const rawImage = el.tags.image || el.tags['image:360'] || el.tags.photo;
            const rawCommons = el.tags['wikimedia_commons'];
            const commonsFromImage = typeof rawImage === 'string' ? buildCommonsUrl(rawImage) : undefined;
            const commonsFromTag = typeof rawCommons === 'string' ? buildCommonsUrl(rawCommons) : undefined;
            if (typeof rawImage === 'string' && /^https?:\/\//i.test(rawImage.trim())) {
              coverImage = rawImage.trim();
            } else if (commonsFromImage) {
              coverImage = commonsFromImage;
            } else if (typeof rawCommons === 'string' && /^https?:\/\//i.test(rawCommons.trim())) {
              coverImage = rawCommons.trim();
            } else if (commonsFromTag) {
              coverImage = commonsFromTag;
            }
          }
          if (!coverImage) {
            // Use placeholder instead of unreliable static map service
            coverImage = '/assets/placeholders/card-header-default.jpg';
          }

          return {
            id,
            title,
            description: undefined,
            address,
            website: (el.tags && (el.tags.website || el.tags['contact:website'] || el.tags.url)) || undefined,
            phone: (el.tags && (el.tags.phone || el.tags['contact:phone'] || el.tags['contact:tel'] || el.tags.tel)) || undefined,
            opening_hours: (el.tags && el.tags.opening_hours) || undefined,
            card_type: el.tags && (el.tags.amenity || el.tags.tourism || el.tags.shop) ? String(el.tags.amenity || el.tags.tourism || el.tags.shop) : 'poi',
            tags: el.tags ? Object.keys(el.tags) : [],
            cover_image: coverImage,
            latitude: typeof lat === 'number' ? lat : undefined,
            longitude: typeof lon === 'number' ? lon : undefined,
            raw: el,
            created_at: new Date().toISOString(),
            creator_id: 'osm',
            visibility: 'public',
            status: 'active',
          };
        });

        // store in cache
        cache.set(cacheKey, { ts: Date.now(), data: mapped });
        retryRef.current = 0;
        if (active) {
          setData(mapped);
          setError(null);
        }
      } catch (err: any) {
        if (!active) return;
        if (err.name === 'AbortError') {
          // aborted, ignore
        } else {
          setError(err?.message ? String(err.message) : 'fetch-error');
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    // debounce rapid changes (e.g., typing in city input)
    if (debounceRef.current) globalThis.clearTimeout(debounceRef.current as any);
    debounceRef.current = (globalThis.setTimeout(() => {
      retryRef.current = 0;
      doFetch();
    }, 700) as unknown) as number;

    return () => { active = false; if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; } };
  }, [city, JSON.stringify(types), enabled, stableEndpoint, stableFetchKey, stableBbox, stableAllowPublic, stableCenterKey]);

  return { data, loading, error } as const;
}
